import React, { useState } from 'react';
import slice from 'lodash/slice';
import map from 'lodash/map';
import get from 'lodash/get';
import find from 'lodash/find';
import findLast from 'lodash/findLast';
import moment from 'moment';
import { Carousel, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { faStar } from '@fortawesome/sharp-solid-svg-icons/faStar';
import { faAward } from '@fortawesome/sharp-solid-svg-icons/faAward';
import { faThumbsUp } from '@fortawesome/sharp-solid-svg-icons/faThumbsUp';

import { faGem } from '@fortawesome/pro-solid-svg-icons/faGem';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faExpand } from '@fortawesome/pro-solid-svg-icons/faExpand';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faChair } from '@fortawesome/pro-solid-svg-icons/faChair';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons/faChevronRight';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons/faChevronLeft';
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons/faMapMarkerAlt';

import { t } from '../../../helpers/t';
import { track, prepareListingPayload } from '../../../helpers/searchResultsAnalytics';

const badges = {
  sponsored: {
    check() { return this.is_boosted; },
    name: 'sponsored',
    icon: faGem,
    class: 'bg-stone-800 text-white',
  },
  top_space: {
    check() { return this.top_spaces_member; },
    name: 'topSpace',
    icon: faAward,
    class: 'bg-amber-500 text-white',
  },
  staff_pick: {
    check() { return this.staff_pick; },
    name: 'staffPick',
    icon: faThumbsUp,
    class: 'bg-blue-500 text-white',
  },
  new: {
    check() { return moment().diff(this.published_at, 'days') <= 3; },
    name: 'new',
    icon: faSparkles,
    class: 'bg-blue-500 text-white',
  },
  fast_responder: {
    check() { return this.response_time_average <= (60 * 60 * 4); },
    name: 'fastResponder',
    icon: faBolt,
    class: 'bg-teal-600 text-white',
  },
};

function getBadge(listing) {
  return find(badges, (badge) => badge.check.apply(listing));
}

function ListingCarousel({
  listing, visible, url, loadASAP,
}) {
  const [currentPhoto, setCurrentPhoto] = useState(0);
  let carousel;

  if (visible) {
    let carouselRef = React.createRef();
    const slicedPhotos = slice(listing.listing_photos, 0, 9); // Get a maximum number of 10 photos
    const photosList = map(slicedPhotos, (photo) => (
      <div key={photo.medium_url}>
        <img className="photo" src={photo.medium_url} alt={`Photo of ${listing.title}`} />
      </div>
    ));

    carousel = (
      <>
        <a
          href={url}
          rel="noopener noreferrer"
          target="_blank"
          onClick={() => {
            track('Listing Search Card Clicked', prepareListingPayload(listing));
          }}
        >
          <Carousel
            className="photos-carousel"
            initialSlide={currentPhoto}
            afterChange={(current) => {
              setCurrentPhoto(current);
              track('Listing Search Card Carousel Viewed', prepareListingPayload(listing));
            }}
            ref={(el) => { carouselRef = el; }}
            effect="scrollx"
            fade={false}
            speed={200}
          >
            {photosList}
          </Carousel>
        </a>
        <div className="carousel-navigation">
          <div className="carousel-button left-button" onClick={() => carouselRef.prev()}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </div>
          <div className="carousel-button right-button" onClick={() => carouselRef.next()}>
            <FontAwesomeIcon icon={faChevronRight} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="photos-carousel-container">
      {carousel}
      {loadASAP ? (
        <picture>
          <source media="(min-width: 1400px)" srcSet={listing.listing_photos[currentPhoto].small_url} />
          <source media="(min-width: 768px)" srcSet={listing.listing_photos[currentPhoto].medium_url} />
          <source media="(max-width: 767px)" srcSet={listing.listing_photos[currentPhoto].small_url} />
          <img className="photo" src={listing.listing_photos[currentPhoto].medium_url} alt={`Photo of ${listing.title}`} />
        </picture>
      ) : (
        <img
          className="photo"
          loading="lazy"
          decoding="async"
          fetchpriority="low"
          src={listing.listing_photos[currentPhoto].medium_url /* Default image for browsers that don't support `srcSet` */ }
          srcSet={`
            ${listing.listing_photos[currentPhoto].small_url ? `${listing.listing_photos[currentPhoto].small_url} 300w,` : ''}
            ${listing.listing_photos[currentPhoto].medium_url} 600w
          `}
          sizes="(max-width: 767px) 30vw, (min-width: 1400px) 20vw, 600px"
          alt="listing"
        />
      )}
    </div>
  );
}

function SecondaryTitle({ space_type_id, area }) {
  const { spaceTypes } = useSelector((state) => state.env);
  const spaceType = get(find(spaceTypes, { id: space_type_id }), 'label');

  return (
    <div
      className="flex items-center font-extralight text-stone-600 text-sm line-clamp-1 overflow-hidden grow"
      title={`${spaceType} - ${area}`}
    >
      <div className="whitespace-nowrap">{spaceType}</div>
      <div className="mx-1">&#8729;</div>
      <div className="line-clamp-1">{area}</div>
    </div>
  )
}

// Utility to prefix language
function withLangPrefix(href, lang = 'en') {
  if (!href.startsWith('/')) return href;
  if (lang === 'en') return href;
  return `/${lang}${href}`;
}

export default function ListingCard({
  listing,
  onMouseEnter,
  onMouseLeave,
  loadASAP,
}) {
  /* Use:
    listing.avg_rating
    listing.avg_accuracy
    listing.avg_interaction
    listing.avg_recommend
  */
  const { userLanguage } = useSelector((state) => state.env) || {};
  const lang = userLanguage || 'en';
  const dispatch = useDispatch();
  const listingLocation = listing.neighborhood || listing.city || listing.state || listing.country;
  const listingBadge = getBadge(listing);
  const translatedTitle = (findLast(listing.content_translations, { column_name: 'title', content_language: userLanguage }) || listing.title)
    || findLast(listing.content_translations, { column_name: 'title', content_language: 'en' });
  const listingTitle = (translatedTitle && translatedTitle.content) || listing.title;

  const [showCarousel, setShowCarousel] = useState(false);
  let showCarouselInterval;
  let listingUrl = `/listings/${listing.id}`;

  if (listing.canonical) {
    listingUrl = `/spaces${listing.canonical}`;
  } else if (listing.external_mappings && listing.external_mappings.ref_number) {
    listingUrl = `/listings/ref/${listing.external_mappings.ref_number}`;
  }

  listingUrl = withLangPrefix(listingUrl, lang);

  function focusOnListingMap({ display_longitude, display_latitude }) {
    jQuery('html, body').animate({ scrollTop: 0 }, 200);
    dispatch({
      type: 'SET_MAP_VIEWPORT',
      payload: {
        longitude: display_longitude,
        latitude: display_latitude,
        zoom: 17,
        transitionDuration: 300,
      },
    });
  }

  return (
    <div className="listing-card" data-id={listing.id} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div
        className="listing-head-container"
        onMouseEnter={() => {
          clearInterval(showCarouselInterval);
          setShowCarousel(true);
        }}
        onMouseLeave={() => {
          clearInterval(showCarouselInterval);
          showCarouselInterval = setTimeout(() => setShowCarousel(false), 250);
        }}
      >
        <Tag
          color="#10BF81"
          className="listing-see-on-map"
          onClick={() => {
            focusOnListingMap(listing);
          }}
        >
          <FontAwesomeIcon icon={faMapMarkerAlt} />
          <div>See on map</div>
        </Tag>

        {listingBadge && (
          <div className={`listing-badge ${listingBadge.name} ${listingBadge.class} in-card`}>
            <FontAwesomeIcon icon={listingBadge.icon}  className="text-xs mr-1 text-stone-600" />
            {t(`factories.listings.listingProperties.${listingBadge.name}`)}
          </div>
        )}
        <ListingCarousel
          listing={listing}
          url={listingUrl}
          visible={showCarousel}
          loadASAP={loadASAP}
        />
      </div>

      <div className="infos">
        {/* Secondary title & Rating */}
        <div className="flex items-center">
          <SecondaryTitle space_type_id={listing.space_type_id} location_type_id={listing.location_type_id} area={listingLocation} />
          {listing.reviews_count > 0 && listing.avg_rating > 0 && (
            <div className="items-center text-stone-800 hidden md:flex">
              <FontAwesomeIcon icon={faStar}  className="text-xs mr-1 text-stone-600" />
              <div className="font-normal text-sm">{listing.avg_rating}<span className="ml-0.5 font-extralight">({listing.reviews_count})</span></div>
            </div>
          )}
        </div>

        {/* Primary title */}
        <a
          className="listing-title text-stone-900 text-base font-normal line-clamp-1 overflow-hidden hover:text-stone-500"
          href={listingUrl}
          title={listingTitle}
          target="_blank"
        >
          {listingTitle}
        </a>

        {/* Icon list */}
        <div className="listing-size-location flex my-0.5 text-stone-600 text-sm font-light">
          <div className="flex items-center" title={`${t('listings.show.sections.information.size')} ${listing.readable_native_size}`}>
            <FontAwesomeIcon icon={faExpand}  className="mr-1 text-xs" />
            {listing.readable_native_size}
          </div>
          {
            listing.standing_capacity && (
              <div
                className="flex ml-2 items-center"
                title={`${t('listings.show.sections.information.standing_capacity.label')} ${t('listings.show.sections.information.standing_capacity.value', { value: listing.standing_capacity })}`}
              >
                <div className="w-[1px] h-3 bg-stone-200"/>
                <FontAwesomeIcon icon={faUser}  className="mx-2 text-xs" />
                {t('listings.show.sections.information.seating_capacity.valueShort', { value: listing.standing_capacity })}
              </div>
            )
          }
          {
            listing.seating_capacity && (
              <div
                className="flex ml-2 items-center"
                title={`${t('listings.show.sections.information.seating_capacity.label')} ${t('listings.show.sections.information.seating_capacity.value', { value: listing.seating_capacity })}`}
              >
                <div className="w-[1px] h-3 bg-stone-200"/>
                <i className="fa-solid fa-chair mx-2 text-xs" />
                {t('listings.show.sections.information.seating_capacity.valueShort', { value: listing.seating_capacity })}
              </div>
            )
          }
        </div>

        {/* Price */}
        <div className="flex">
          {
            listing.readable_native_daily_price
              ? (
                <div className="listing-price font-light text-stone-900 text-sm grow">
                  <div
                    dangerouslySetInnerHTML={
                      {
                        __html: t('directives.listing.price.daily', {
                          listing,
                          interpolation: { escapeValue: false }
                        })
                      }
                    }
                  />
                </div>
              )
              : (
                <div className="listing-price grow" title={t('directives.listing.priceUponRequest')}>
                  {t('directives.listing.priceUponRequest')}
                </div>
              )
          }
        </div>

        <div className="listing-badges flex md:hidden">
          {listingBadge && (
            <div className={`listing-badge ${listingBadge.name} ${listingBadge.class} mr-2`}>
              <FontAwesomeIcon icon={listingBadge.icon}  className="text-xs mr-1 text-stone-600" />
              {t(`factories.listings.listingProperties.${listingBadge.name}`)}
            </div>
          )}
          {listing.reviews_count > 0 && listing.avg_rating > 0 && (
            <div className="flex items-center text-stone-800 bg-stone-100 px-1 rounded">
              <FontAwesomeIcon icon={faStar}  className="text-xs mr-1 text-stone-600" />
              <div className="font-normal text-sm">{listing.avg_rating}<span className="ml-0.5 font-extralight">({listing.reviews_count})</span></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { default as ListingCardSkeleton } from './ListingCardSkeleton';
